import { Grid } from '@material-ui/core'
import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { EnumTipoSujeto } from '../../../../api'
import { ButtonOption } from '../../../../components/shared/splitButton/splitButton'
import { enumEnumToValueNumber } from '../../../../helpers'
import { hasResource } from '../../../../helpers/permissionsHelper'
import { useConfirmationDialog } from '../../../../hooks'
import { NonAuthRoutes } from '../../../../model'
import { Resources } from '../../../../model/RBAC/constants'
import { InformationDialogs } from '../../../../model/enums/informationDialogs'
import {
  fetchClientCommercialsByID,
  fetchClientContractsByID,
  fetchClientDataByID
} from '../../../../redux'
import {
  selectClientCommercials,
  selectClientContracts,
  selectClientData,
  selectIsFetchingClientCommercials,
  selectIsFetchingClientCommercialsError,
  selectIsFetchingClientContracts,
  selectIsFetchingClientContractsError,
  selectIsFetchingClientData,
  selectIsFetchingClientDataError
} from '../../../../redux/selectors/client.selectors'
import { selectCompanyCommodities } from '../../../../redux/selectors/profile.selectors'
import { getClientName, isSubjectExtendedClient, isSubjectFinalClient } from '../../client.helper'
import { commercialsToTableModelMapper, contractsListMapper } from '../../client.mapper'
import { useStyles } from '../client.file.styles'
import { CommercialsCard } from '../components/commercialsCard/commercialsCard'
import { ContactsCard } from '../components/contactsCard/contactsCard'
import { ContractsCard } from '../components/contractsCard/contractsCard'
import { ContractAddButtonOptions } from '../components/contractsCard/contractsCard.types'
import { ExternalsCard } from '../components/externalsCard/externalsCard'
import { InfoCard } from '../components/infoCard/infoCard'
import { ProceduresCard } from '../components/proceduresCard/proceduresCard'

export interface ClientFileMainProps {
  clientID: number
  // onShowAdditional: () => void
  // commodities: EnumCommodity[] | undefined
}

export const ClientFileMain: React.FC<ClientFileMainProps> = ({ clientID }) => {
  const [t] = useTranslation(['globalerisLibrary', 'clients', 'clientFile', 'enums'])
  const history = useHistory()
  const classes = useStyles()

  const { getInformation, closeInformation } = useConfirmationDialog()

  const clientData = useSelector(selectClientData)
  const contractsData = contractsListMapper(useSelector(selectClientContracts))
  const commercialsData = commercialsToTableModelMapper(useSelector(selectClientCommercials))
  const isFetchingContracts = useSelector(selectIsFetchingClientContracts)
  const isFetchingCommercials = useSelector(selectIsFetchingClientCommercials)
  const isFetchingClient = useSelector(selectIsFetchingClientData)
  const commodities = useSelector(selectCompanyCommodities)
  const fetchingClientError = useSelector(selectIsFetchingClientDataError)
  const fetchingContractsError = useSelector(selectIsFetchingClientContractsError)
  const fetchingCommercialsError = useSelector(selectIsFetchingClientCommercialsError)

  const [refreshProcedureCardComponent, setRefreshProcedureCardComponent] =
    React.useState<string>('')

  const getAddress = useCallback(() => {
    return clientData?.fiscalAddress
      ? `${clientData.fiscalAddress.postalAddress}, ${clientData.fiscalAddress.municipality}, ${clientData.fiscalAddress.province}, ${clientData.fiscalAddress.country}`
      : ''
  }, [clientData])

  const handleClickInfo = () => {
    history.push(NonAuthRoutes.ClientEdit, {
      clientID: clientID,
      subjectType: clientData?.type
        ? enumEnumToValueNumber(EnumTipoSujeto, clientData?.type)
        : EnumTipoSujeto.ClienteFinal
    })
  }

  const handleOnAddContact = () => {
    history.push(NonAuthRoutes.ContactInfo, {
      id: clientID,
      subjectType: clientData?.type ?? EnumTipoSujeto.ClienteFinal
    })
  }

  const handleOnAddContract = (selectedOption: ButtonOption) => {
    if (!clientData) {
      return
    }

    const subjectType = enumEnumToValueNumber(EnumTipoSujeto, clientData?.type)

    switch (selectedOption.name) {
      case ContractAddButtonOptions.DEFAULT: {
        if (subjectType) {
          switch (subjectType) {
            case EnumTipoSujeto.Comercial:
              history.push(NonAuthRoutes.CommercialEdit, {
                commercialId: clientID,
                activeTabPanel: 'contracts'
              })
              break
            case EnumTipoSujeto.Mayorista:
              history.push(NonAuthRoutes.partnersEdit, {
                idCliente: clientID,
                activeTabPanel: 'contracts'
              })
              break
            default:
              history.push(NonAuthRoutes.clientsModEdit, {
                idCliente: clientID,
                activeTabPanel: 'contracts'
              })
          }
        }
        break
      }
      case ContractAddButtonOptions.CHANGE_OWNER:
        history.push(NonAuthRoutes.ContractChangeOwner)
        break
      case ContractAddButtonOptions.GAS:
        history.push(NonAuthRoutes.ContractAdd, {
          clientID: clientID,
          commodity: selectedOption.id,
          pathName: NonAuthRoutes.ClientFile
        })
        break
      case ContractAddButtonOptions.POWER:
        history.push(NonAuthRoutes.ContractAdd, {
          clientID: clientID,
          commodity: selectedOption.id,
          pathName: NonAuthRoutes.ClientFile
        })
        break
      case ContractAddButtonOptions.COMMERCIAL:
        history.push(NonAuthRoutes.ContractAdd, {
          clientID: clientID,
          commodity: null,
          pathName: NonAuthRoutes.ClientFile
        })
        break
    }
  }

  const handleOnEditContracts = () => {
    const subjectType = enumEnumToValueNumber(EnumTipoSujeto, clientData?.type)
    if (subjectType) {
      if (subjectType === EnumTipoSujeto.ClienteFinal) {
        history.push(NonAuthRoutes.clientsModEdit, {
          idCliente: clientID,
          activeTabPanel: 'contracts'
        })
      } else if (subjectType === EnumTipoSujeto.Mayorista) {
        history.push(NonAuthRoutes.partnersEdit, {
          idCliente: clientID,
          activeTabPanel: 'contracts'
        })
      } else if (subjectType === EnumTipoSujeto.Comercial) {
        history.push(NonAuthRoutes.CommercialEdit, {
          commercialId: clientID,
          activeTabPanel: 'contracts'
        })
      }
    } else {
      history.push(NonAuthRoutes.clientsModEdit, {
        idCliente: clientID,
        activeTabPanel: 'contracts'
      })
    }
  }

  const handleRefreshProcedureCard = (refreshComponent: string) => {
    setRefreshProcedureCardComponent(refreshComponent)
  }

  const getContracts = async () => {
    fetchClientContractsByID(clientID)
  }

  const getClientData = () => {
    fetchClientDataByID(clientID)
  }

  const getCommercials = (clientID: number) => {
    fetchClientCommercialsByID(clientID)
  }

  // Mounting
  useEffect(() => {
    const showAnnouncement = async () => {
      await getInformation(InformationDialogs.COMMUNICATION_NEW_PAGE_CLIENT_FILE).then(res =>
        closeInformation()
      )
    }
    showAnnouncement()

    if (clientID) {
      if (!clientData || clientID !== clientData.id) {
        getClientData()
        getContracts()
        getCommercials(clientID)
      }
    } else {
      history.goBack()
    }
  }, [])

  return (
    <div className={classes.root}>
      <Grid container spacing={4}>
        <Grid item container xs={12} md={6} lg={5} spacing={2} style={{ alignSelf: 'self-start' }}>
          <Grid item xs={12}>
            <InfoCard
              mainTitle={getClientName(clientData)}
              isFetching={isFetchingClient}
              error={fetchingClientError}
              texts={[
                `${clientData?.documentID ?? ''}`,
                `${clientData?.subtype ?? ''}`,
                `${clientData?.state ?? ''}`,
                (clientData?.clientCode || clientData?.erpCode) ?? '',
                getAddress()
              ]}
              onClick={handleClickInfo}
              onRetry={() => getClientData()}
            />
          </Grid>
          <Grid item xs={12}>
            <ContactsCard
              contacts={clientData?.contactInfo}
              isFetching={isFetchingClient}
              error={fetchingClientError}
              onClickAdd={handleOnAddContact}
              onRetry={() => getClientData()}
            />
          </Grid>
          {hasResource(Resources.Commercial) && isSubjectFinalClient(clientData?.type) && (
            <Grid item xs={12}>
              <CommercialsCard
                commercials={commercialsData}
                isFetchingClient={isFetchingClient}
                isFetchingCommercials={isFetchingCommercials}
                error={fetchingCommercialsError}
                onRetry={() => getClientData()}
              />
            </Grid>
          )}
          {hasResource(Resources.ComunicationsCenterExternals) &&
            (isSubjectFinalClient(clientData?.type) ||
              isSubjectExtendedClient(clientData?.type)) && (
              <Grid item xs={12}>
                <ExternalsCard
                  isFetchingClient={isFetchingClient}
                  error={fetchingCommercialsError}
                  onRetry={() => getClientData()}
                  clientID={clientID}
                />
              </Grid>
            )}
        </Grid>
        <Grid item container xs={12} lg={7} spacing={2} style={{ alignContent: 'flex-start' }}>
          <Grid item xs={12}>
            <ContractsCard
              contracts={contractsData}
              onClickAdd={handleOnAddContract}
              onClickEdit={handleOnEditContracts}
              isFetching={isFetchingContracts}
              isFetchingClient={isFetchingClient}
              commodities={commodities}
              subjectType={clientData?.type}
              error={fetchingContractsError}
              onRetry={() => getContracts()}
              onRefreshProcedureCard={handleRefreshProcedureCard}
            />
          </Grid>
          {hasResource(Resources.ComunicationsCenterProcedures) &&
            (isSubjectFinalClient(clientData?.type) ||
              isSubjectExtendedClient(clientData?.type)) && (
              <Grid item xs={12}>
                <ProceduresCard
                  clientID={clientID}
                  isFetching={isFetchingContracts}
                  isFetchingClient={isFetchingClient}
                  error={fetchingContractsError}
                  onRetry={() => getContracts()}
                  refreshComponent={refreshProcedureCardComponent}
                />
              </Grid>
            )}
        </Grid>
      </Grid>
    </div>
  )
}
